/**
 * Centralized API Client
 * Handles all API requests with proper error handling and authentication
 */
import axios from 'axios';

// Import these lazily to avoid circular dependencies
let router;
let store;

// Import router and store lazily when needed
const getRouter = () => {
  if (!router) {
    router = import('@/router').then(module => module.default);
  }
  return router;
};

const getStore = () => {
  if (!store) {
    store = import('@/store').then(module => module.default);
  }
  return store;
};

// Create base API client
const apiClient = axios.create({
  baseURL: import.meta.env.VITE_APP_API_URL || 'https://api.vesproc.com',
  headers: {
    'Content-Type': 'application/json',
  }
});

// Request interceptor - adds auth token to all requests
apiClient.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token');
    const isLoginPage = window.location.pathname === '/login' || 
                        window.location.pathname === '/register' || 
                        window.location.pathname === '/forgot-password' ||
                        window.location.pathname === '/reset-password' ||
                        window.location.pathname === '/set-password';
    
    // If no token and on auth pages, allow request (for login/register endpoints)
    // Otherwise, add token if available
    if (token) {
      config.headers['Authorization'] = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
    } else if (!isLoginPage) {
      // If no token and not on auth pages, this might be a logout scenario
      // Still allow the request but it will likely fail with 401, which is handled by response interceptor
    }
    
    return config;
  },
  error => Promise.reject(error)
);

// Helper functions to reduce complexity
const isAuthPage = () => {
  const path = window.location.pathname;
  return path === '/login' || 
         path === '/register' || 
         path === '/forgot-password' ||
         path === '/reset-password' ||
         path === '/set-password';
};

const getCurrentPath = () => {
  return window.location.pathname + window.location.search;
};

const redirectToLogin = async (currentPath) => {
  try {
    const routerModule = await getRouter();
    if (routerModule) {
      routerModule.push({
        path: '/login',
        query: { redirect: currentPath }
      });
    } else {
      window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`;
    }
  } catch (err) {
    console.error('Error redirecting to login:', err);
    window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`;
  }
};

const handleStoreLogout = async (isLoginPage) => {
  try {
    const storeModule = await getStore();
    if (!storeModule) return;
    
    if (storeModule.dispatch) {
      await storeModule.dispatch('auth/logout');
      if (!isLoginPage) {
        storeModule.dispatch('notify', {
          message: 'Your session has expired. Please log in again.',
          type: 'error',
          title: 'Session Expired'
        });
      }
    } else {
      storeModule.commit('auth/LOGOUT');
    }
  } catch (err) {
    console.error('Error handling store logout:', err);
  }
};

const handle401Error = async (originalRequest) => {
  originalRequest._retry = true;
  const isLoginPage = isAuthPage();
  
  if (isLoginPage) {
    return;
  }
  
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  
  const currentPath = getCurrentPath();
  await handleStoreLogout(isLoginPage);
  await redirectToLogin(currentPath);
};

// Response interceptor - handles auth errors
apiClient.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;
    const hasToken = localStorage.getItem('token');
    const isLoginPage = isAuthPage();
    
    if (!hasToken && isLoginPage) {
      return Promise.reject(error);
    }
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      await handle401Error(originalRequest);
    }
    
    return Promise.reject(error);
  }
);

export default apiClient; 